/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import {
  useCallback,
  useMemo,
  useEffect,
  useState,
} from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import { useSnackbar } from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import useConfig from '../config/useConfig';
import Context from './context';
import { useAPI } from '../api';
import useLoadStory from './effects/useLoadStory';
import useSaveStory from './actions/useSaveStory';
import useGeneratePages from './actions/useGeneratePages';
import useHashState from './effects/useHashState';
import useHistoryEntry from './effects/useHistoryEntry';
import useHistoryReplay from './effects/useHistoryReplay';
import useStoryReducer from './useStoryReducer';
import useAutoSave from './actions/useAutoSave';
import { StoryTriggersProvider } from './storyTriggers';

/**
 * Shared reference to an empty array for cases where it is important to avoid
 * returning a new array reference on every invocation, as in a connected or
 * other pure component which performs `shouldComponentUpdate` check on props.
 */
const EMPTY_ARRAY = [];

function StoryProvider({ storyId, children }) {
  const [pagesWithVisibilityUpdates, setPagesWithVisibilityUpdates] = useState(
    []
  );
  const { isDemo } = useConfig();
  const [hashPageId, setHashPageId] = useHashState('page', null);
  const {
    state: reducerState,
    api,
    internal: { restore },
  } = useStoryReducer({
    current: hashPageId,
  });
  const {
    pages,
    current,
    selection,
    savedColors,
    story,
    animationState,
    capabilities,
    deletedPagesIds,
  } = reducerState;

  useEffect(() => setHashPageId(current), [current, setHashPageId]);

  const {
    actions: { togglePageHidden: togglePageHiddenApi },
  } = useAPI();

  const { showSnackbar } = useSnackbar();

  // Generate current page info.
  const { currentPageId, currentPageIndex, currentPageNumber, currentPage } =
    useMemo(() => {
      if (!current) {
        return {
          currentPageId: null,
          currentPageIndex: null,
          currentPageNumber: null,
          currentPage: null,
        };
      }
      const index = pages.findIndex(({ id }) => id === current);
      const number = index + 1;
      const page = pages[index];
      return {
        currentPageId: current,
        currentPageIndex: index,
        currentPageNumber: number,
        currentPage: page,
      };
    }, [pages, current]);

  // Generate selection info
  const {
    selectedElementIds,
    selectedElements,
    selectedElementAnimations,
    hasSelection,
  } = useMemo(() => {
    if (!currentPage) {
      return {
        selectedElements: EMPTY_ARRAY,
        selectedElementIds: EMPTY_ARRAY,
        selectedElementAnimations: EMPTY_ARRAY,
        hasSelection: false,
      };
    }

    const els = currentPage.elements.filter(({ id }) => selection.includes(id));
    const animations = (currentPage.animations || []).reduce(
      (acc, { targets, ...properties }) => {
        if (targets.some((id) => selection.includes(id))) {
          return [...acc, { targets, ...properties }];
        }

        return acc;
      },
      []
    );

    return {
      selectedElementIds: selection.length > 0 ? selection : EMPTY_ARRAY,
      selectedElements: els.length > 0 ? els : EMPTY_ARRAY,
      selectedElementAnimations:
        animations.length > 0 ? animations : EMPTY_ARRAY,
      hasSelection: els.length > 0,
    };
  }, [currentPage, selection]);

  // This effect loads and initialises the story on first load (when there's no pages).
  const shouldLoad = pages.length === 0;
  useLoadStory({ restore, shouldLoad, storyId, isDemo });

  // These effects send updates to and restores state from history.
  useHistoryEntry({
    pages,
    current,
    savedColors,
    selection,
    story,
    capabilities,
  });
  useHistoryReplay({ restore });

  // This action allows the user to save the story
  // (and it will have side-effects because saving can update url and status,
  //  thus the need for `updateStory`)
  const { updateStory } = api;

  const { saveStory, isSaving, isFreshlyPublished } = useSaveStory({
    storyId,
    pages,
    story,
    updateStory,
  });

  const { generatePages, isGeneratingPages } = useGeneratePages({
    storyId,
    story,
    pages,
    saveStory,
    deletedPagesIds,
  });

  const { autoSave, isAutoSaving } = useAutoSave({
    storyId,
    pages,
    story,
  });

  const togglePageHidden = useCallback(
    ({ pageId, cmsPageId, isHidden, ...props }, ...rest) => {
      api.updatePageProperties({
        pageId,
        properties: { hidden: isHidden },
      });

      const successMessage = isHidden
        ? __('Page was set as hidden.', 'web-stories')
        : __('Page was set as visible.', 'web-stories');

      // if there's no CMS page ID, the page was created in Studio,
      // and its visibility will be saved when the story is saved
      if (!cmsPageId) {
        showSnackbar({
          message: successMessage,
          dismissable: true,
        });
        return;
      }

      setPagesWithVisibilityUpdates((currentPagesWithVisibilityUpdates) => [
        ...currentPagesWithVisibilityUpdates,
        pageId,
      ]);

      togglePageHiddenApi({ pageId: cmsPageId, isHidden })
        .then(() => {
          showSnackbar({
            message: successMessage,
            dismissable: true,
          });
        })
        .catch(() => {
          showSnackbar({
            message: __('Page visibility could not be updated.', 'web-stories'),
            dismissable: true,
          });

          // If the toggle failed, we need to revert the change
          api.updatePageProperties({
            pageId,
            properties: { hidden: !isHidden },
          });
        })
        .finally(() => {
          setPagesWithVisibilityUpdates((currentPagesWithVisibilityUpdates) =>
            currentPagesWithVisibilityUpdates.filter((id) => id !== pageId)
          );
        });
    },
    [
      api.updatePageProperties,
      showSnackbar,
      togglePageHiddenApi,
      setPagesWithVisibilityUpdates,
    ]
  );

  const fullStory = useMemo(
    () => ({
      pages,
      pagesWithVisibilityUpdates,
      currentPageId,
      currentPageIndex,
      currentPageNumber,
      currentPage,
      selectedElementIds,
      selectedElements,
      selectedElementAnimations,
      hasSelection,
      savedColors,
      story,
      animationState,
      capabilities,
      deletedPagesIds,
      meta: {
        isSaving: isSaving || isAutoSaving,
        isSavingStory: isSaving,
        isAutoSavingStory: isAutoSaving,
        isFreshlyPublished,
        isGeneratingPages,
      },
    }),
    [
      pages,
      pagesWithVisibilityUpdates,
      currentPageId,
      currentPageIndex,
      currentPageNumber,
      currentPage,
      selectedElementIds,
      selectedElements,
      selectedElementAnimations,
      hasSelection,
      savedColors,
      story,
      animationState,
      capabilities,
      isSaving,
      isAutoSaving,
      isFreshlyPublished,
      isGeneratingPages,
      deletedPagesIds,
    ]
  );

  const state = {
    state: fullStory,
    actions: {
      ...api,
      autoSave,
      saveStory,
      generatePages,
      togglePageHidden,
    },
    internal: { reducerState, restore },
  };

  return (
    <Context.Provider value={state}>
      <StoryTriggersProvider story={fullStory}>
        {children}
      </StoryTriggersProvider>
    </Context.Provider>
  );
}

StoryProvider.propTypes = {
  children: PropTypes.node,
  storyId: PropTypes.number,
};

export default StoryProvider;
